import { formatPropertyName, decodeHtmlEntities, applyTagFields, normalizeToCsvString } from './utils/format';

// ─── window.dataLayer helpers ──────────────────────────────────────────────

function getDataLayerEvent(eventName: string): any {
  const datalayer = (window as any).dataLayer;
  if (!Array.isArray(datalayer)) return undefined;
  const index = datalayer.findIndex((el: any) => el.event === eventName);
  return index !== -1 ? datalayer[index] : undefined;
}

function getPDPVersion(): string {
  const userDetails = getDataLayerEvent('user-details');
  if (userDetails?.eventInfo?.userType === 'instructor' && userDetails?.eventInfo?.persona?.segment === 'HE') {
    return 'Instructor View';
  } else if (userDetails?.eventInfo?.userType === 'learner' && userDetails?.eventInfo?.persona?.segment === 'HE') {
    return 'Student View';
  }
  return 'Guest View';
}

function getTabTriggerEl(): HTMLElement | null {
  const tabPane = document.querySelector('.tab-pane.active');
  if (tabPane?.id) {
    return document.querySelector(`[data-bs-target='#${tabPane.id}']`);
  }
  return null;
}

function getHePdpData(): Record<string, any> | null {
  const productDetail = getDataLayerEvent('product-page-details');
  if (productDetail?.product && (productDetail.product.isHePdp || productDetail.product.isProfPdp)) {
    const data: Record<string, any> = {
      productType: productDetail.product.productType,
      pdpVersion: getPDPVersion(),
      programSourceId: productDetail.product.programSourceId,
      isbn10: productDetail.product.isbn,
      isbn13: productDetail.product.isbn13,
      isEvergreenPDP: productDetail.product.isEvergreenPDP,
      isEdoms: productDetail.product.isEdoms,
      isProfPdp: !!productDetail.product.isProfPdp,
      pdpType: productDetail.product.pdpType,
      editionOrRelease: productDetail.product.editionOrRelease,
    };
    if (productDetail.product.isProfPdp) {
      data.businessUnit = productDetail.product.businessUnit;
    }
    const tabButton = getTabTriggerEl();
    if (tabButton?.dataset?.layerPdpViewType) {
      data.pdpViewType = tabButton.dataset.layerPdpViewType;
    }
    return data;
  }
  return null;
}

function addBreadcrumbAnalyticData(element: Element): Record<string, any> {
  if (element.classList.contains('breadcrumb-content')) {
    return {
      parent: Array.from(element.closest('.breadcrumb')?.querySelectorAll('li') ?? [])
        .map((o) => o.textContent?.trim())
        .join(' / '),
    };
  }
  return {};
}

function getFullPath(): string {
  return window.location.origin + window.location.pathname;
}

function hasFilterData(el: Element): boolean {
  return (
    el.hasAttribute('data-filter-menu-id') &&
    el.hasAttribute('data-filter-result-count') &&
    el.hasAttribute('data-filter-text')
  );
}

function getFilterData(el: Element): Record<string, string | null> {
  return {
    menuId: el.getAttribute('data-filter-menu-id'),
    resultCount: el.getAttribute('data-filter-result-count'),
    filterText: el.getAttribute('data-filter-text'),
  };
}

function clearFilterData(el: Element): void {
  el.removeAttribute('data-filter-menu-id');
  el.removeAttribute('data-filter-result-count');
  el.removeAttribute('data-filter-text');
}

// ─── Main exported function ────────────────────────────────────────────────

/**
 * Builds the analytics payload for any .mh-analytic element.
 * Faithfully ported from datalayer.js getCommonAnalytics().
 */
export function getCommonAnalytics(element: Element): Record<string, any> {
  const data: Record<string, any> = {};
  if (!element) return data;

  // PDP context
  const hePDPData = getHePdpData();
  if (hePDPData) Object.assign(data, hePDPData);

  // Breadcrumb
  Object.assign(data, addBreadcrumbAnalyticData(element));

  // data-layer-* attributes → camelCase keys
  const htmlEl = element as HTMLElement;
  Object.keys(htmlEl.dataset).forEach((key) => {
    if (htmlEl.dataset[key] && key.startsWith('layer')) {
      data[formatPropertyName(key)] = htmlEl.dataset[key];
    }
  });

  // id + label
  data.id = data.id || element.closest('[id]')?.id;
  data.label = data.label || (element as HTMLElement).innerText || (element as HTMLElement).ariaLabel;

  // Modal / collapse subarea context
  if (htmlEl.dataset.layerSubarea === 'modal') {
    data.modalId = element.closest('.modal')?.id ?? '';
  }
  if (htmlEl.dataset.layerSubarea === 'collapse') {
    data.collapseId = element.closest('.collapse')?.id ?? '';
  }

  // Modal ancestor context
  const modalEl = element.closest('.modal');
  if (modalEl) {
    data.modalId = modalEl.id;
    data.regionId = modalEl.getAttribute('data-layer-region-id');
    data.regionName = modalEl.getAttribute('data-layer-region-label');
  }

  // Image overlay context
  const overlayEl = element.closest('.image-overlay-content');
  if (overlayEl) {
    data.regionId = overlayEl.getAttribute('data-layer-region-id');
    data.regionName = overlayEl.getAttribute('data-layer-region-label');
    data.overlayId = overlayEl.id;
  }

  // data-area-name ancestor → subarea
  const areaElement = element.closest('[data-area-name]') as HTMLElement | null;
  if (areaElement?.dataset?.areaName) {
    data.subarea = areaElement.dataset.areaName;
  }

  // href (avoid javascript:void(0))
  const anchor = element as HTMLAnchorElement;
  if (anchor.href) {
    data.href =
      anchor.href !== 'javascript:void(0)'
        ? anchor.href
        : `${getFullPath()}#${element.id}`;
  }

  // aria-expanded — intentionally inverted (BS hasn't toggled yet at click time)
  const ariaExpanded = (element as HTMLElement).ariaExpanded;
  if (!htmlEl.dataset.skipAriaExpandedReverseLogic && ariaExpanded) {
    data.expanded = ariaExpanded === 'true' ? 'false' : 'true';
  } else {
    data.expanded = (ariaExpanded === 'true').toString();
  }

  // aria-selected — intentionally inverted
  const ariaSelected = (element as HTMLElement).ariaSelected;
  if (ariaSelected) {
    data.selected = ariaSelected === 'true' ? 'false' : 'true';
  }

  // Active tab type
  const tabButton = getTabTriggerEl();
  if (tabButton?.dataset?.layerTabType) {
    data.tabType = tabButton.dataset.layerTabType;
  }

  // Tab label from tabpanel ancestor
  const tabPane = element.closest('[role="tabpanel"]');
  if (tabPane?.id) {
    const tab = document.querySelector(`[data-bs-target='#${tabPane.id}']`);
    if (tab?.hasAttribute('data-layer-label')) {
      data.tab = tab.getAttribute('data-layer-label');
    }
  }

  // Purchase options duration type
  if (
    element.hasAttribute('data-layer-comp') &&
    element.getAttribute('data-layer-comp') === 'purchaseoptions' &&
    element.hasAttribute('data-layer-duration-type')
  ) {
    data.durationType = element.getAttribute('data-layer-duration-type');
  }

  // Global nav filter data
  if (data['subarea'] === 'globalnav') {
    const globalNavEl = element.closest('#global-nav');
    if (globalNavEl && hasFilterData(globalNavEl)) {
      Object.assign(data, getFilterData(globalNavEl));
      clearFilterData(globalNavEl);
    }
  }

  // data-additional-datalayer-values JSON attribute
  const addValuesAttr =
    element.getAttribute('data-additional-datalayer-values') ||
    htmlEl.dataset.additionalDatalayerValues;
  if (addValuesAttr) {
    const parsed = JSON.parse(decodeHtmlEntities(addValuesAttr));
    if (parsed?.supportSite) data.supportSite = parsed.supportSite;
    if (parsed?.programsSolutions && !data.supportProducts) {
      data.supportProducts = normalizeToCsvString(parsed.programsSolutions);
    }
    if (parsed?.filterTagsJson) applyTagFields(data, parsed.filterTagsJson);
  }

  // Support site from article ancestor
  if (!data.supportSite) {
    const articleEl = element.closest('article.mhecomm-support-content-fragment');
    if (articleEl?.classList.contains('article-content')) {
      const supportSiteAttr = (articleEl as HTMLElement).dataset.layerSupportSite;
      if (supportSiteAttr) data.supportSite = supportSiteAttr;
    }
  }

  // Tag fields from article ancestor
  if (!data.tags && !data.supportPersona && !data.supportTopics && !data.businessUnit && !data.supportProducts) {
    const articleEl = element.closest('article.mhecomm-support-content-fragment');
    if (articleEl?.classList.contains('article-content') && (articleEl as HTMLElement).dataset.layerTags) {
      applyTagFields(data, JSON.parse(decodeHtmlEntities((articleEl as HTMLElement).dataset.layerTags!)));
    }
  }

  // Business unit
  if (!data.businessUnit) {
    const bu = element.getAttribute('data-layer-business-unit');
    if (bu) data.businessUnit = bu;
  }
  if (!data.businessUnit) {
    const buContainer = element.closest('[data-business-unit]') as HTMLElement | null;
    if (buContainer?.dataset?.businessUnit) data.businessUnit = buContainer.dataset.businessUnit;
  }

  // Animation (data-sal)
  const animationEl = (htmlEl.hasAttribute('data-sal') ? htmlEl : element.closest('[data-sal]')) as HTMLElement | null;
  if (animationEl) {
    data.animation = {
      type: animationEl.dataset.sal,
      settings: {
        effect: animationEl.dataset.salEasing,
        delay: animationEl.dataset.salDelay,
        duration: animationEl.dataset.salDuration,
      },
    };
  }

  // HE Events timeslots (in getCommonAnalytics for general usage — social share handler also uses this)
  const eventTimeslots = document.querySelector('.event-timeslots');
  if (eventTimeslots) {
    const timeslotText = Array.from(eventTimeslots.querySelectorAll('li'))
      .map((o) => (o.querySelector('time') as HTMLElement)?.textContent?.trim())
      .filter(Boolean)
      .join(', ');
    data['event-timeslot'] = timeslotText;
  }

  return data;
}
